import React from "react";
import { Check } from "lucide-react";
import { USER_FACING_SPORTS, getSportMeta } from "../../lib/sportCategories";

/**
 * Multi-select sport picker for USER PROFILE and REGISTRATION ONBOARDING.
 *
 * Users can belong to multiple communities (a roller skater who also
 * BMXes). Renders as a tappable grid where selected sports show a tick.
 *
 * Props:
 *  - value: string[] — currently selected sports (order-insensitive)
 *  - onChange: (nextSports: string[]) => void
 *  - minSelected: number — required minimum (e.g. 1 on registration)
 *  - label: string — optional label above the grid
 */
export const SportMultiSelect = ({
  value = [],
  onChange,
  minSelected = 0,
  label = null,
  className = "",
}) => {
  const toggle = (sportId) => {
    if (value.includes(sportId)) {
      // Prevent going below minSelected threshold.
      if (value.length <= minSelected) return;
      onChange(value.filter((s) => s !== sportId));
    } else {
      onChange([...value, sportId]);
    }
  };

  return (
    <div
      data-testid="sport-multi-select"
      className={`space-y-2 ${className}`}
      role="group"
      aria-label={label || "Select your communities"}
    >
      {label && (
        <div className="text-sm font-medium text-muted-foreground">{label}</div>
      )}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        {USER_FACING_SPORTS.map((sportId) => {
          const meta = getSportMeta(sportId);
          const active = value.includes(sportId);
          return (
            <button
              key={sportId}
              type="button"
              onClick={() => toggle(sportId)}
              data-testid={`sport-multi-option-${sportId}`}
              aria-pressed={active}
              className="relative flex items-center gap-2 rounded-xl px-3 py-3 text-sm font-medium transition-all touch-manipulation backdrop-blur-sm"
              style={{
                backgroundColor: active ? `${meta.color}33` : "rgba(255,255,255,0.04)",
                border: `1.5px solid ${active ? meta.color : "rgba(255,255,255,0.15)"}`,
                color: active ? meta.color : "inherit",
                boxShadow: active ? `0 4px 12px ${meta.color}40` : "none",
              }}
            >
              {meta.emoji && (
                <span className="text-xl" aria-hidden>
                  {meta.emoji}
                </span>
              )}
              <span className="text-left leading-tight flex-1">{meta.label}</span>
              {active && (
                <Check
                  className="h-4 w-4 shrink-0"
                  style={{ color: meta.color }}
                  data-testid={`sport-multi-check-${sportId}`}
                  aria-hidden
                />
              )}
            </button>
          );
        })}
      </div>
      {value.length === 0 && minSelected > 0 && (
        <p className="text-xs text-muted-foreground italic">
          Pick at least {minSelected} to personalise your feed.
        </p>
      )}
    </div>
  );
};

export default SportMultiSelect;
